import React from 'react';

import styled from '@emotion/styled';

import {
  BREAKPOINTS,
  disabledColor,
  getColWidthCalc,
  unit,
  warningColor,
  white,
} from '@zego/theme';
import ThemePurple from '@zego/themes/ThemePurple';

import H1 from '@zego/components/atoms/H1';
import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import ContentContainer from '@zego/components/ContentContainer';
import Logo from '@zego/components/header/Logo';
import AboutContent from '@zego/components/promotions/AboutContent';
import PromotionsHero from '@zego/components/promotions/Hero';
import PartnersWhyZego from '@zego/components/promotions/PartnersWhyZego';

import heroImage from '@zego/static/images/hero-generic.jpg';
import Choice from '@zego/static/svgs/Choice.svg';
import CustomerService from '@zego/static/svgs/CustomerService.svg';
import HiddenFees from '@zego/static/svgs/HiddenFees.svg';
import Paper from '@zego/static/svgs/Paperless.svg';
import UberEats from '@zego/static/svgs/partners/uber-eats.svg';

const Title = styled(H1)({
  color: white,
  marginBottom: unit(3),
});

const SubTitle = styled(RegularText)({
  color: white,
  fontSize: 18,
  lineHeight: '24px',
  marginBottom: unit(6),
  maxWidth: 700,
});

const REASONS = [
  {
    title: 'No hidden admin fees',
    description: 'We won’t ever charge you for changing your details.',
    icon: HiddenFees,
  },
  {
    title: 'Friendly customer service',
    description: 'On hand to help you seven days a week.',
    icon: CustomerService,
  },
  {
    title: 'Paperless',
    description:
      'All your important documents are safely secured in the Zego app.',
    icon: Paper,
  },
  {
    title: 'Flexible',
    description: 'We build your insurance around you and the way you work.',
    icon: Choice,
  },
];

const UberPageWrapper = ({ banner, children, scrollToSalesLead }) => (
  <ThemePurple>
    {banner && (
      <>
        {banner}
        <Spacer height={unit(1.5)} />
      </>
    )}
    <ContentContainer
      css={{
        zIndex: 1000,
        position: 'relative',
        [BREAKPOINTS.normal]: {
          paddingTop: 50,
        },
      }}
    >
      <View
        css={{
          flexGrow: 0,
          display: 'flex',
          alignItems: 'center',
          paddingTop: unit(3),
          fill: white,
        }}
      >
        <Logo />
        <Spacer width={unit(2)} />
        <RegularText css={{ fontSize: 32, color: white, opacity: 0.8 }}>
          +
        </RegularText>
        <Spacer width={unit(2)} />
        <img
          alt="Uber Eats"
          css={{ display: 'inline-block' }}
          src={UberEats}
        />
      </View>
      <Spacer height={unit(3)} />
    </ContentContainer>

    <PromotionsHero image={heroImage} bgBlend="#00000088">
      <Title>Get on the road with Uber Eats today!</Title>
      <SubTitle>
        Zego creates policies with Uber Eats courier partner in mind, meaning
        you get the same flexibility from your insurance as you do from your
        work.
      </SubTitle>
    </PromotionsHero>

    <Spacer height={unit(4)} />
    <ContentContainer>
      <View
        css={{
          [BREAKPOINTS.medium]: {
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row-reverse',
          },
        }}
      >
        <View
          css={{
            [BREAKPOINTS.medium]: { width: getColWidthCalc(12, 20, 5) },
          }}
        >
          <View
            css={{
              display: 'block',
              [BREAKPOINTS.medium]: { display: 'none' },
            }}
          >
            <AboutContent
              para1="As well as offering 30-day and annual policies, Zego is the only pay-as-you-go Delivery insurance provider in the UK. Cover is now available for cars, scooters and motorcycles!"
              para2="Simply choose the type of insurance you require and we’ll give you a quote in minutes."
            />
          </View>
          <div
            onClick={scrollToSalesLead}
            css={{
              position: 'relative',
              paddingBottom: unit(4),
              cursor: 'pointer',
              [BREAKPOINTS.medium]: { paddingBottom: 0 },
            }}
          >
            <View
              css={{
                position: 'relative',
                margin: '0 auto',
                width: '150px',
                height: '150px',
                backgroundColor: warningColor(5),
                borderRadius: '75px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                right: 0,
                [BREAKPOINTS.medium]: { position: 'absolute', top: -170 },
              }}
            >
              <RegularText
                css={{
                  textAlign: 'center',
                  color: white,
                  fontSize: 18,
                  fontWeight: 'bold',
                }}
              >
                Pay-as-you-go,
                <br />
                30-Day
                <br />
                or Annual
              </RegularText>
            </View>
          </div>
          {children}
          <Spacer height={unit(4)} />
        </View>
        <View
          css={{
            display: 'flex',
            flexDirection: 'column-reverse',
            [BREAKPOINTS.medium]: {
              width: getColWidthCalc(12, 20, 6),
              flexDirection: 'column',
            },
          }}
        >
          <View
            css={{
              display: 'none',
              [BREAKPOINTS.medium]: { display: 'block' },
            }}
          >
            <Spacer height={unit(4)} />
            <AboutContent
              para1="As well as offering 30-day and annual policies, Zego is the only pay-as-you-go Delivery insurance provider in the UK. Cover is now available for cars, scooters and motorcycles!"
              para2="Simply choose the type of insurance you require and we’ll give you a quote in minutes."
            />
          </View>
          <Spacer height={unit(2)} />
          <div>
            <PartnersWhyZego reasons={REASONS} />
          </div>
          <Spacer height={unit(4)} />
          <View>
            <div>
              <RegularText
                css={{ fontStyle: 'italic', color: disabledColor(10) }}
              >
                “A brilliant system and a life saver in recent time. Thanks
                Team Zego - you are truly 5*”{' '}
                <span css={{ fontWeight: 'bold' }}>
                  - Aftab Javaid, Uber Eats courier partner
                </span>
              </RegularText>
            </div>

            <RegularText
              css={{
                color: disabledColor(10),
                fontWeight: 'bold',
              }}
            />
            <Spacer height={unit(2)} />
            <RegularText
              css={{ fontWeight: 'bold', color: disabledColor(10) }}
            >
              It doesn’t matter if you’re currently working with Uber Eats, or
              you’re yet to complete your first session. You can get a free
              quote from us.
            </RegularText>
            <Spacer height={unit(4)} />
          </View>
        </View>
      </View>
    </ContentContainer>
  </ThemePurple>
);

export default UberPageWrapper;
